// Main application component
// Handles routing between different views based on user role and authentication state
// CHANGE: Simplified getDefaultView - was a 4-case switch always returning 'tasks'
// CHANGE: Removed Planning page and all references to PlanningView
// CHANGE: Unified TeamView component now used for both manager and admin roles (role-based permissions)
// UPDATED: Integrated ToastProvider for centralized toast notification management
// CHANGE: Director role now has access to all admin views in read-only mode (users, sites, services, templates, team, incidents)
// CHANGE: Manager role now has access to incidents view for their assigned sites
// REFACTORED: Unified navigation for all roles - all use Layout component with centralized routing via App.tsx
// REMOVED: UnifiedView component - replaced with direct view rendering through Layout for consistency
// CHANGE: All user roles (admin, director, manager, operator) now land on Accueil (tasks) page by default after login
// CHANGE: Added ErrorBoundary around AppContent to catch unhandled JS errors and prevent white screen
// CHANGE: Removed all debug console statements for production
// UPDATED: Added AnalyticsView for KPI system v1 (managers, directors, admins)

import { useState, useEffect, useCallback } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { LoginForm } from './components/LoginForm';
import { Layout } from './components/Layout';
import { UserManagement } from './components/admin/UserManagement';
import { SiteManagement } from './components/admin/SiteManagement';
import { ServiceManagement } from './components/admin/ServiceManagement';
import { IncidentManagement } from './components/admin/IncidentManagement';
import { TeamView } from './components/shared/TeamView';
import { TemplateLibrary } from './components/templates/TemplateLibrary';
import { MyTasksView } from './components/shared/MyTasksView';
import { PlaceholderView } from './components/placeholder/PlaceholderView';
import { ForcedPasswordChangeModal } from './components/ForcedPasswordChangeModal';
import { AnalyticsView } from './components/analytics/AnalyticsView';
import { ErrorBoundary } from './components/ErrorBoundary';
import { addBreadcrumb } from './lib/sentry';

function AppContent() {
  const { user, loading, refreshUser } = useAuth();
  const [currentView, setCurrentView] = useState('');

  const handleNavigate = useCallback((view: string) => {
    addBreadcrumb('navigation', `Navigation vers ${view}`, { view });
    setCurrentView(view);
  }, []);

  // Reset currentView when user changes (login/logout/role change)
  useEffect(() => {
    if (!user) {
      // Clear the view when user logs out
      setCurrentView('');
    }
  }, [user?.id]); // Trigger when user identity changes

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Chargement...</div>
      </div>
    );
  }

  if (!user) {
    return <LoginForm />;
  }

  // Check if user must change password (blocks all access)
  if (user.must_change_password) {
    return (
      <>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Changement de mot de passe requis</h2>
            <p className="text-gray-600">Vous devez changer votre mot de passe pour continuer.</p>
          </div>
        </div>
        <ForcedPasswordChangeModal
          onSuccess={refreshUser}
          reason="admin_reset"
        />
      </>
    );
  }

  const getDefaultView = () => 'tasks';

  // Get list of views accessible to current user role
  const getAuthorizedViews = (): string[] => {
    switch (user.role) {
      case 'admin':
        return ['users', 'sites', 'services', 'templates', 'team', 'incidents', 'tasks', 'analytics'];
      case 'director':
        return ['users', 'sites', 'services', 'templates', 'team', 'incidents', 'tasks', 'analytics'];
      case 'manager':
        return ['tasks', 'team', 'incidents', 'analytics'];
      case 'operator':
        return ['tasks', 'incidents'];
      default:
        return [];
    }
  };

  const authorizedViews = getAuthorizedViews();

  // Security check: if currentView is set but not authorized, redirect to default
  const view = (currentView && authorizedViews.includes(currentView))
    ? currentView
    : getDefaultView();

  const renderView = () => {
    switch (view) {
      case 'users':
        return <UserManagement />;
      case 'sites':
        return <SiteManagement />;
      case 'services':
        return <ServiceManagement />;
      case 'templates':
        return <TemplateLibrary />;
      case 'team':
        return <TeamView />;
      case 'incidents':
        return <IncidentManagement />;
      case 'analytics':
        return <AnalyticsView />;
      case 'tasks':
        return <MyTasksView />;
      default:
        return (
          <PlaceholderView
            title="Bienvenue"
            description="Sélectionnez une vue dans le menu."
          />
        );
    }
  };

  // All roles now use Layout component with centralized navigation
  return (
    <Layout
      currentView={view}
      onNavigate={handleNavigate}
    >
      {renderView()}
    </Layout>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ToastProvider>
          <AppContent />
        </ToastProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
